import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { CodeExample } from './CodeExample';
import { ErrorReference } from './ErrorReference';
import { WebhooksGuide } from './WebhooksGuide';
import { Badge } from '@/components/ui/badge';

export function GettingStarted() {
  const apiKey = localStorage.getItem('apiKey') || 'your-api-key-here';
  const baseUrl = window.location.origin;

  const listAppsCurl = `curl -X GET "${baseUrl}/api/v1/apps?external_user_id=user-123&page=1&limit=20" \\
  -H "X-API-Key: ${apiKey}"`;

  const deployAppCurl = `curl -X POST "${baseUrl}/api/v1/apps/deploy" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${apiKey}" \\
  -d '{
    "app_name": "wordpress",
    "stack_name": "basic",
    "external_user_id": "user-123",
    "display_name": "John Doe",
    "env_vars": { "WP_DEBUG": "false" }
  }'`;

  const checkStatusCurl = `curl -X GET "${baseUrl}/api/v1/apps/1" \\
  -H "X-API-Key: ${apiKey}"`;

  const meta = { request_id: 'req-abc123', timestamp: '2026-10-15T10:00:00Z' };

  const listAppsResponse = {
    data: {
      data: [
        {
          id: 1,
          name: 'wordpress-user-123-1792050000',
          full_subdomain: 'wordpress-user-123-1792050000.example.com',
          status: 'running',
          external_user_id: 'user-123',
          display_name: 'John Doe',
          app_catalog: { id: 1, name: 'wordpress', description: 'WordPress blogging platform' },
          stack: { name: 'basic', cpu_limit: 1, shared_cpu: false, memory_mb: 512 },
          endpoint: { id: 1, name: 'production-1' },
          created_at: '2026-10-15T09:00:00Z',
        },
      ],
      page: 1,
      limit: 20,
      total: 1,
      total_pages: 1,
    },
    meta,
  };

  const deployAppResponse = {
    data: {
      app_id: 1,
      deployment_id: 42,
      status: 'pending',
      full_subdomain: 'wordpress-user-123-1792050000.example.com',
      endpoint: { id: 1, name: 'production-1' },
    },
    meta,
  };

  const checkStatusResponse = {
    data: {
      id: 1,
      name: 'wordpress-user-123-1792050000',
      status: 'running',
      full_subdomain: 'wordpress-user-123-1792050000.example.com',
      deployment: { id: 42, status: 'success', created_at: '2026-10-15T09:00:00Z' },
      endpoint: { id: 1, name: 'production-1', url: 'https://portainer.example.com' },
    },
    meta,
  };

  return (
    <Card>
      <CardHeader className="space-y-0 pb-2">
        <CardTitle className="text-lg">Getting Started</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
          {/* Authentication */}
          <section>
            <h3 className="text-base font-semibold mb-2">Authentication</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Every <code className="text-blue-600">/api/v1</code> request requires an API key in the <code className="text-blue-600">X-API-Key</code> header (<code className="text-blue-600">/health</code> is public).
            </p>
            <div className="rounded-lg bg-muted border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline">Header</Badge>
                <code className="text-sm font-mono">X-API-Key</code>
              </div>
              <p className="text-sm text-muted-foreground">
                Your API key can be generated and managed from the <a href="/api-keys" className="text-blue-600 hover:underline">API Keys</a> page.
              </p>
            </div>
          </section>

          {/* Base URL */}
          <section>
            <h3 className="text-base font-semibold mb-2">Base URL</h3>
            <p className="text-sm text-muted-foreground mb-2">
              All API endpoints are relative to this base URL:
            </p>
            <div className="rounded-lg bg-muted border p-4">
              <code className="text-sm font-mono text-green-600">{baseUrl}/api/v1</code>
            </div>
          </section>

          {/* Common Operations */}
          <section>
            <h3 className="text-base font-semibold mb-2">Common Operations</h3>
            <Tabs defaultValue="list">
              <TabsList className="mb-4">
                <TabsTrigger value="list">List Apps</TabsTrigger>
                <TabsTrigger value="deploy">Deploy App</TabsTrigger>
                <TabsTrigger value="status">Check App</TabsTrigger>
              </TabsList>
              <TabsContent value="list" className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  List one SaaS user's apps (page/limit pagination; the page is in data.data).
                </p>
                <CodeExample code={listAppsCurl} language="bash" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Response:</p>
                  <CodeExample
                    code={JSON.stringify(listAppsResponse, null, 2)}
                    language="json"
                  />
                </div>
              </TabsContent>
              <TabsContent value="deploy" className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Deploy a catalog app for a user. Returns 201 right away; the app starts as pending.
                </p>
                <CodeExample code={deployAppCurl} language="bash" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Response:</p>
                  <CodeExample
                    code={JSON.stringify(deployAppResponse, null, 2)}
                    language="json"
                  />
                </div>
              </TabsContent>
              <TabsContent value="status" className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Poll the app until status is running (or failed). deployment.status tracks the deploy job.
                </p>
                <CodeExample code={checkStatusCurl} language="bash" />
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Response:</p>
                  <CodeExample
                    code={JSON.stringify(checkStatusResponse, null, 2)}
                    language="json"
                  />
                </div>
              </TabsContent>
            </Tabs>
          </section>

          {/* Webhooks */}
          <section>
            <h3 className="text-base font-semibold mb-2">Webhooks</h3>
            <WebhooksGuide baseUrl={baseUrl} apiKey={apiKey} />
          </section>

          {/* Error Handling */}
          <section>
            <ErrorReference />
          </section>
        </CardContent>
      </Card>
  );
}